import React, { VFC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { Box, Grid, makeStyles, FileItem, Alert } from '@dxb-web-ui-framework/design-system';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { fundTransfersService } from '../../../../../../channels/secure-web/services';
import { Document as TransferDocument } from '../../../../../fundTransfer/src/domain/Transfer/entities/Document';
import { ConsolidatedTransactionDetails } from '../../../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { SkeletonWrapperList } from '../Skeleton';
import { TransferDoc, TransferUploader } from '.';

const useStyles = makeStyles(theme => ({
  retry: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  fileItemText: {
    '& p:first-of-type': {
      'overflow-wrap': 'anywhere',
    },
    maxHeight: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'unset',
      padding: `${theme.spacing(0)}px ${theme.spacing(3)}px ${theme.spacing(3)}px`,
    },
  },
}));

type TransferDocState = {
  transactionId: number;
  meta: {
    timestamp: string;
  };
};

export type TransferDocumentsProps = {
  transactionDetails: ConsolidatedTransactionDetails;
  isStandalonePage: boolean;
  shouldHideDeleteButton?: boolean;
};

const TransferDocuments: VFC<TransferDocumentsProps> = ({
  transactionDetails,
  isStandalonePage,
  shouldHideDeleteButton,
}) => {
  const [transfersDoc, setTransfersDoc] = useState<Nullable<TransferDocState>>({
    transactionId: transactionDetails.transactionId,
    meta: { timestamp: new Date().toISOString() },
  });
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    data: transactionDocuments,
    error: transactionDocumentsError,
    isLoading: isLoadingTransactionDocuments,
  } = useInvokeSWRService<TransferDocument[]>(
    fundTransfersService.GetDocuments,
    {
      transactionId: transactionDetails.transactionId,
      meta: transfersDoc?.meta,
    },
    Boolean(transfersDoc),
  );

  const fetchTransactionDocuments = () => {
    setTransfersDoc({ transactionId: transactionDetails.transactionId, meta: { timestamp: new Date().toISOString() } });
  };

  return (
    <Grid
      container
      item
      xs={isStandalonePage ? 12 : 4}
      justifyContent='center'
      alignContent='center'
      className={classes.container}>
      {!isEmpty(transactionDocumentsError) && (
        <Alert
          icon={false}
          severity='error'
          variant='filled'
          action={
            <Box
              title={t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Retry')}
              onClick={fetchTransactionDocuments}
              className={classes.retry}>
              {t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Retry')}
            </Box>
          }>
          {t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Error.Message')}
        </Alert>
      )}
      <Box className={classes.fileItemText}>
        <TransferUploader
          isLoadingTransactionDocuments={isLoadingTransactionDocuments}
          onRefetchData={fetchTransactionDocuments}
          transferService={fundTransfersService}
          transactionId={transactionDetails.transactionId}
        />
        {isEmpty(transactionDocumentsError) &&
          !isLoadingTransactionDocuments &&
          transactionDocuments?.map(({ fileName, fileData, fileDescription, fileId }) => (
            <Box key={fileName + fileName} mt={0.5}>
              <TransferDoc
                transferService={fundTransfersService}
                fileName={fileName}
                fileData={fileData}
                fileDescription={fileDescription}
                transactionId={transactionDetails.transactionId}
                fileId={fileId}
                onRefetchData={fetchTransactionDocuments}
                shouldHideDeleteButton={shouldHideDeleteButton}
              />
            </Box>
          ))}
        {isLoadingTransactionDocuments && (
          <SkeletonWrapperList
            skeletonsToDisplay={2}
            Component={FileItem}
            boxProps={{ padding: 0, width: '100%' }}
            paperProps={{ elevation: 0 }}
            componentProps={{ hasSkeleton: true }}
          />
        )}
      </Box>
    </Grid>
  );
};

export { TransferDocuments };
